import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHead from '../../components/SEOHead';
import img1 from '../../assets/marketplaces2.png';
import img2 from '../../assets/marketplaces.png';
import './ServicePage.css';

const SEO = {
  title: 'Nykaa Seller Management Services | Mad Marketing Club',
  description: 'Nykaa brand seller management services — catalog optimization, Nykaa Ads, brand storefront setup, beauty marketplace SEO & revenue growth strategies. Free consultation.',
  canonical: 'https://madmarketingclub.com/services/nykaa',
  breadcrumb: [
    { name: 'Home', url: 'https://madmarketingclub.com/' },
    { name: 'Services', url: 'https://madmarketingclub.com/#services' },
    { name: 'Nykaa', url: 'https://madmarketingclub.com/services/nykaa' },
  ],
  service: { name: 'Nykaa Seller Management Services', type: 'Nykaa Marketplace Management' },
};

const OVERVIEW_POINTS = [
  'Access millions of beauty & fashion shoppers',
  'Build brand visibility in premium categories',
  'Sell across beauty, wellness, fashion, and personal care',
  'Participate in Nykaa sales campaigns',
  'Improve brand positioning',
  'Leverage marketplace advertising opportunities',
  'Access customer-focused beauty commerce ecosystem',
  'Scale through category-specific growth opportunities',
];

const APPROACH = [
  'Nykaa seller onboarding',
  'Product catalog optimization',
  'Nykaa SEO',
  'Nykaa Ads management',
  'Brand storefront enhancement',
  'Inventory forecasting',
  'Pricing optimization',
  'Campaign management',
  'Seller performance monitoring',
  'Revenue growth strategies',
];

const PROCESS = [
  {
    num: '01',
    title: 'Brand Consultation & Discovery',
    intro: 'We begin by understanding:',
    points: [
      'Product categories',
      'Brand positioning',
      'Target audience',
      'Competitor landscape',
      'Current marketplace presence',
      'Growth objectives',
    ],
  },
  {
    num: '02',
    title: 'Nykaa Marketplace Audit',
    intro: 'We evaluate:',
    points: [
      'Product listings',
      'Visual branding',
      'Search visibility',
      'Product content quality',
      'Pricing competitiveness',
      'Inventory performance',
      'Seller account health',
    ],
  },
  {
    num: '03',
    title: 'Marketplace Growth Strategy',
    intro: 'Our customized roadmap includes:',
    points: [
      'Catalog optimization strategy',
      'SEO enhancement plan',
      'Advertising roadmap',
      'Inventory planning',
      'Campaign participation strategy',
      'Brand visibility improvements',
    ],
  },
  {
    num: '04',
    title: 'Execution & Optimization',
    intro: 'We implement:',
    points: [
      'Product listings',
      'SEO improvements',
      'Catalog enhancements',
      'Advertising campaigns',
      'Store optimization',
      'Pricing updates',
      'Inventory improvements',
    ],
  },
  {
    num: '05',
    title: 'Reporting & Scaling',
    intro: 'Continuous monitoring includes:',
    points: [
      'Sales growth',
      'Product visibility',
      'Advertising ROI',
      'Conversion rates',
      'Inventory health',
      'Campaign performance',
      'Growth opportunities',
    ],
  },
];

const SERVICES = [
  {
    title: 'Nykaa Seller Account Setup & Onboarding',
    gradient: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
    points: [
      'Seller registration support',
      'Brand onboarding guidance',
      'Documentation assistance',
      'Product category setup',
      'Store profile optimization',
      'Marketplace onboarding',
      'Initial account configuration',
    ],
  },
  {
    title: 'Product Listing & Catalog Management',
    gradient: 'linear-gradient(135deg, #2563eb, #0891b2)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14,2 14,8 20,8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10,9 9,9 8,9" />
      </svg>
    ),
    points: [
      'Product listing creation',
      'Catalog uploads',
      'Product attribute optimization',
      'Variant setup',
      'Listing issue resolution',
      'Product data enhancement',
      'Catalog maintenance',
      'Premium product presentation',
    ],
  },
  {
    title: 'Nykaa SEO Services',
    gradient: 'linear-gradient(135deg, #7c3aed, #a855f7)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
    points: [
      'Keyword research',
      'Competitor analysis',
      'Product title optimization',
      'Description optimization',
      'Product attribute enhancement',
      'Search visibility improvement',
      'Conversion-focused content strategies',
    ],
  },
  {
    title: 'Nykaa Advertising Management',
    gradient: 'linear-gradient(135deg, #ea580c, #f59e0b)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
        <line x1="2" y1="20" x2="22" y2="20" />
      </svg>
    ),
    points: [
      'Sponsored product campaigns',
      'Visibility campaigns',
      'Campaign optimization',
      'Budget management',
      'Keyword targeting',
      'ROAS optimization',
      'Promotional visibility scaling',
    ],
  },
  {
    title: 'Brand Store & Creative Optimization',
    gradient: 'linear-gradient(135deg, #db2777, #e879f9)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    ),
    points: [
      'Brand store optimization',
      'Product image enhancement',
      'Infographic design',
      'Lifestyle creative consultation',
      'Product merchandising',
      'Brand positioning support',
      'Visual content optimization',
    ],
  },
  {
    title: 'Inventory & Order Management',
    gradient: 'linear-gradient(135deg, #16a34a, #10b981)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
        <line x1="12" y1="22.08" x2="12" y2="12" />
      </svg>
    ),
    points: [
      'Inventory forecasting',
      'Stock planning',
      'Inventory monitoring',
      'Order tracking support',
      'Return management guidance',
      'Replenishment planning',
      'Inventory health optimization',
    ],
  },
  {
    title: 'Pricing & Promotion Management',
    gradient: 'linear-gradient(135deg, #0369a1, #0891b2)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
    ),
    points: [
      'Pricing strategy development',
      'Competitive pricing analysis',
      'Promotion planning',
      'Discount strategy management',
      'Campaign pricing optimization',
      'Seasonal sale preparation',
    ],
  },
  {
    title: 'Seller Performance & Account Health Management',
    gradient: 'linear-gradient(135deg, #b45309, #d97706)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    ),
    points: [
      'Seller account monitoring',
      'Compliance management',
      'Operational issue resolution',
      'Product policy guidance',
      'Account optimization',
      'Marketplace support coordination',
    ],
  },
  {
    title: 'Analytics & Reporting',
    gradient: 'linear-gradient(135deg, #475569, #334155)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    points: [
      'Sales reports',
      'Advertising reports',
      'Product performance analysis',
      'Profitability tracking',
      'Inventory reports',
      'Campaign performance analysis',
      'Marketplace growth dashboards',
    ],
  },
];

const DIFFERENTIATORS = [
  'Dedicated marketplace specialists',
  'Beauty & lifestyle marketplace expertise',
  'SEO + Advertising integration',
  'Premium catalog optimization',
  'Performance-driven strategies',
  'Transparent reporting',
  'Scalable growth planning',
  'End-to-end marketplace management',
];

const INDUSTRIES = [
  'Beauty & Cosmetics',
  'Skincare',
  'Haircare',
  'Personal Care',
  'Wellness Products',
  'Fashion & Apparel',
  'Luxury Beauty Brands',
  'Organic & Natural Products',
  'Fragrance Brands',
  'D2C Beauty Brands',
];

const STAGES = [
  'New product launch strategies',
  'Visibility enhancement',
  'Product positioning',
  'Launch campaign optimization',
  'Nykaa sale event planning',
  'Festival campaign management',
  'Sponsored promotions',
  'Visibility scaling strategies',
];

const ADDITIONAL = [
  'Product portfolio expansion',
  'Category diversification',
  'Brand visibility scaling',
  'Long-term marketplace growth strategies',
  'Profitability analysis',
  'Competitor benchmarking',
  'Review & rating monitoring',
  'Seasonal campaign preparation',
];

const FAQS = [
  {
    q: 'Do you help with Nykaa seller onboarding?',
    a: 'Yes. We assist with seller registration, documentation, category approvals, store setup, and marketplace onboarding.',
  },
  {
    q: 'Can you manage Nykaa advertising campaigns?',
    a: 'Yes. We create, optimize, and scale marketplace advertising campaigns focused on visibility, traffic, and sales growth.',
  },
  {
    q: 'Do you optimize Nykaa product listings?',
    a: 'Yes. We optimize titles, descriptions, keywords, product attributes, imagery recommendations, and catalog structures to improve rankings and conversions.',
  },
  {
    q: 'Can you support beauty and skincare brands?',
    a: 'Yes. We specialize in beauty, skincare, wellness, and lifestyle marketplace optimization strategies tailored for Nykaa.',
  },
  {
    q: 'Do you provide reporting?',
    a: 'Yes. We provide detailed reports covering sales, advertising performance, profitability, inventory, and marketplace growth opportunities.',
  },
];

export default function NykaaPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const toggle = (i) => setOpenFaq(openFaq === i ? null : i);

  return (
    <>
      <SEOHead {...SEO} faqs={FAQS} />
      <Header />
      <main className="sp-page">

        {/* BREADCRUMB */}
        <nav className="sp-breadcrumb">
          <div className="sp-breadcrumb-inner">
            <Link to="/">Home</Link>
            <span className="sp-bc-sep">/</span>
            <Link to="/#services">Services</Link>
            <span className="sp-bc-sep">/</span>
            <span className="sp-bc-current">Nykaa Marketplace</span>
          </div>
        </nav>

        {/* HERO */}
        <section className="sp-hero">
          <div className="sp-hero-inner">
            <div className="sp-hero-tag">Nykaa Marketplace</div>
            <h1 className="sp-hero-h1">Nykaa Marketplace Management Services</h1>
            <p className="sp-hero-sub">Complete Nykaa Seller Account Management & Growth Solutions</p>
            <p className="sp-hero-desc">Grow your beauty, wellness, fashion, or personal care brand on Nykaa with professional marketplace management services designed to increase product visibility, improve conversions, optimize advertising campaigns, and scale revenue. From seller onboarding and catalog optimization to Nykaa Ads, inventory planning, fulfillment support, and marketplace growth strategies, we help brands build a profitable presence on Nykaa.</p>
            <div className="sp-hero-stats">
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">Beauty</span><span className="sp-hero-stat-lbl">Focused Platform</span></div>
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">Premium</span><span className="sp-hero-stat-lbl">Brand Presence</span></div>
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">Full</span><span className="sp-hero-stat-lbl">Account Management</span></div>
            </div>
            <div className="sp-hero-btns">
              <a href="/#contact" className="sp-btn-primary">Get Free Audit</a>
              <a href="#services" className="sp-btn-outline">Explore Services</a>
            </div>
          </div>
        </section>

        {/* PLATFORM OVERVIEW — with Image 1 */}
        <section className="sp-sec" id="overview">
          <div className="sp-sec-inner">
            <div className="sp-sec-img-grid">
              <div>
                <div className="sp-label">Platform Overview</div>
                <h2 className="sp-h2">Sell & Scale on Nykaa with a Performance-Driven Strategy</h2>
                <p className="sp-para">Nykaa is one of India's leading beauty, wellness, fashion, and lifestyle e-commerce platforms. Nykaa enables brands and sellers to reach a large customer base through its online marketplace while providing tools for catalog management, brand promotion, logistics support, campaign participation, analytics, and customer engagement.</p>
                <p className="sp-para">Selling on Nykaa gives your brand:</p>
                <ul className="sp-bullet-list">
                  {OVERVIEW_POINTS.map((pt, i) => <li key={i} className="sp-bullet-item"><span className="sp-dot" />{pt}</li>)}
                </ul>
              </div>
              <div className="sp-img-box">
                <img src={img1} alt="Nykaa marketplace management" />
              </div>
            </div>
          </div>
        </section>

        {/* HOW WE HELP */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <div className="sp-label">How We Help Your Brand</div>
            <h2 className="sp-h2">End-to-End Nykaa Marketplace Management</h2>
            <p className="sp-para">Success on Nykaa requires more than uploading products. Marketplace growth depends on optimized listings, strong visual branding, SEO-focused product content, advertising strategy, pricing competitiveness, inventory planning, and account management.</p>
            <h3 className="sp-h3">We work as your dedicated Nykaa growth partner and manage:</h3>
            <div className="sp-approach-grid">
              {APPROACH.map((item, i) => <div key={i} className="sp-approach-tag">{item}</div>)}
            </div>
            <p className="sp-para">We focus on sustainable growth through premium catalog optimization, brand-focused strategies, and conversion-focused execution tailored for the Nykaa beauty and lifestyle marketplace.</p>
          </div>
        </section>

        {/* OUR PROCESS */}
        <section className="sp-sec" id="process">
          <div className="sp-sec-inner">
            <div className="sp-label">Our Process</div>
            <h2 className="sp-h2">How We Work</h2>
            <div className="sp-process-list">
              {PROCESS.map((step, i) => (
                <div key={i} className="sp-process-step">
                  <div className="sp-process-left">
                    <div className="sp-process-num">{step.num}</div>
                    {i < PROCESS.length - 1 && <div className="sp-process-line" />}
                  </div>
                  <div className="sp-process-body">
                    <h3 className="sp-process-title">{step.title}</h3>
                    <p className="sp-process-intro">{step.intro}</p>
                    <ul className="sp-process-subs">
                      {step.points.map((pt, j) => <li key={j} className="sp-process-sub"><span className="sp-dot-sm" />{pt}</li>)}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section className="sp-sec sp-sec--alt" id="services">
          <div className="sp-sec-inner">
            <div className="sp-label">Services We Provide</div>
            <h2 className="sp-h2">Everything Your Nykaa Business Needs</h2>
            <div className="sp-cards">
              {SERVICES.map((s, i) => (
                <div key={i} className="sp-card">
                  <div className="sp-card-accent" style={{ background: s.gradient }} />
                  <div className="sp-card-icon" style={{ background: s.gradient }}>{s.icon}</div>
                  <h3 className="sp-card-title">{s.title}</h3>
                  <ul className="sp-card-list">
                    {s.points.map((pt, j) => <li key={j} className="sp-card-item"><span className="sp-dot-sm" />{pt}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY CHOOSE — with Image 2 */}
        <section className="sp-sec">
          <div className="sp-sec-inner">
            <div className="sp-label">Why Choose Our Nykaa Marketplace Services</div>
            <h2 className="sp-h2">Marketplace Experts Focused on Beauty & Lifestyle Growth</h2>
            <p className="sp-para">We combine marketplace strategy, Nykaa SEO, premium catalog optimization, advertising management, inventory planning, and branding expertise to help brands grow successfully on Nykaa.</p>
            <div className="sp-why-grid">
              <div>
                <h3 className="sp-h3">Benefits of Working With Us</h3>
                <ul className="sp-diff-list">
                  {DIFFERENTIATORS.map((d, i) => <li key={i} className="sp-diff-item"><span className="sp-check" />{d}</li>)}
                </ul>
              </div>
              <div className="sp-img-box">
                <img src={img2} alt="Nykaa marketplace management approach" />
              </div>
            </div>
            <h3 className="sp-h3">Industries We Serve</h3>
            <div className="sp-industry-wrap">
              {INDUSTRIES.map((ind, i) => <span key={i} className="sp-industry-chip">{ind}</span>)}
            </div>
          </div>
        </section>

        {/* BUSINESS STAGE */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <h2 className="sp-h2">Marketplace Growth Solutions for Every Stage</h2>
            <p className="sp-para">Whether you're looking to grow through:</p>
            <div className="sp-stage-grid">
              {STAGES.map((s, i) => <div key={i} className="sp-stage-item"><span className="sp-dot" />{s}</div>)}
            </div>
            <p className="sp-para">Our team helps create a structured marketplace growth strategy tailored to your beauty and lifestyle brand goals on Nykaa.</p>
          </div>
        </section>

        {/* ADDITIONAL SERVICES */}
        <section className="sp-sec">
          <div className="sp-sec-inner">
            <div className="sp-label">Additional Growth Services</div>
            <div className="sp-additional-grid">
              {ADDITIONAL.map((item, i) => <div key={i} className="sp-additional-item"><span className="sp-check" />{item}</div>)}
            </div>
          </div>
        </section>

        {/* FAQ — accordion */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <div className="sp-label">Frequently Asked Questions</div>
            <h2 className="sp-h2">Got Questions? We Have Answers.</h2>
            <div className="sp-faq-list">
              {FAQS.map((faq, i) => (
                <div key={i} className={`sp-faq-card${openFaq === i ? ' sp-faq-card--open' : ''}`}>
                  <button className="sp-faq-header" onClick={() => toggle(i)} aria-expanded={openFaq === i}>
                    <span className="sp-faq-q">{faq.q}</span>
                    <span className="sp-faq-chevron">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                    </span>
                  </button>
                  {openFaq === i && <div className="sp-faq-body"><div className="sp-faq-a">{faq.a}</div></div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="sp-cta">
          <div className="sp-cta-inner">
            <h2 className="sp-cta-h2">Ready to Grow on Nykaa?</h2>
            <p className="sp-cta-desc">Scale your brand with complete Nykaa Marketplace Management Services focused on premium catalog optimization, advertising performance, inventory planning, brand visibility, and long-term marketplace growth.</p>
            <a href="/#contact" className="sp-btn-white">Get Free Audit</a>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
